

import React, { useContext, useState, useEffect } from 'react';
import { AppContext, supabase } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { PlanSuscripcion, Rol, SuscripcionEstado } from '../types';

const SuscripcionBadge: React.FC<{ status: SuscripcionEstado }> = ({ status }) => {
    const styles: Record<SuscripcionEstado, string> = {
        trialing: 'bg-indigo-100 text-indigo-800',
        active: 'bg-green-100 text-green-800',
        past_due: 'bg-red-100 text-red-800',
        canceled: 'bg-gray-100 text-gray-800',
    };
     const labels: Record<SuscripcionEstado, string> = {
        trialing: 'En Prueba',
        active: 'Activa',
        past_due: 'Vencida',
        canceled: 'Cancelada',
    };
    return <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${styles[status]}`}>{labels[status]}</span>;
}

const UsageProgressBar: React.FC<{ count: number; limit: number }> = ({ count, limit }) => {
    if (limit === -1) {
        return (
            <div>
                <div className="flex justify-between text-sm font-medium text-gray-600 mb-1">
                    <span>{count} Socios</span>
                    <span>Sin límite</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div className="bg-blue-500 h-2.5 rounded-full" style={{ width: `100%` }}></div>
                </div>
            </div>
        );
    }
    const percentage = limit > 0 ? (count / limit) * 100 : 0;
    
    let barColor = 'bg-blue-500';
    if (percentage > 90) barColor = 'bg-red-500';
    else if (percentage > 75) barColor = 'bg-yellow-500';

    return (
        <div>
            <div className="flex justify-between text-sm font-medium text-gray-600 mb-1">
                <span>{count} / {limit} Socios</span>
                <span>{percentage.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div className={`${barColor} h-2.5 rounded-full`} style={{ width: `${Math.min(percentage, 100)}%` }}></div>
            </div>
        </div>
    );
};

export default function SuscripcionScreen(): React.ReactElement {
    const { user, club, socios, planes, isReadOnly, facturas, paymentSuccessMessage, setPaymentSuccessMessage } = useContext(AppContext);
    const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);
    const [isManaging, setIsManaging] = useState(false);
    const [errorGestion, setErrorGestion] = useState<string | null>(null);


    const ui = useUI();

    useEffect(() => {
        if (paymentSuccessMessage) {
            const timer = setTimeout(() => {
                setPaymentSuccessMessage(null);
            }, 5000); // El mensaje desaparece después de 5 segundos

            return () => clearTimeout(timer);
        }
    }, [paymentSuccessMessage, setPaymentSuccessMessage]);

    if (user?.rol !== Rol.ADMIN) {
        return <p>Acceso denigado.</p>;
    }

    if (!club) {
        return <p>Cargando información del club...</p>
    }
    
    const handlePlanChange = async (plan: PlanSuscripcion) => {
        if (!club || !user) return;
        setLoadingPlanId(plan.id);
        try {
            const { data, error } = await supabase.functions.invoke('crear-suscripcion', {
                body: { 
                    planId: plan.id,
                    clubId: club.id,
                    userEmail: user.email,
                    clubNombre: club.nombre
                },
            });

            if (error) {
                // El objeto de error de Supabase anida el error real.
                // Lo lanzamos para que sea capturado por el bloque catch.
                throw error;
            }

            if (data.checkoutUrl) {
                window.location.href = data.checkoutUrl;
            } else if (data.error) {
                 throw new Error(data.error);
            } else {
                 throw new Error("La respuesta de la función no incluyó una URL de checkout.");
            }

        } catch (err: any) {
            console.error("Error completo al procesar el cambio de plan:", JSON.stringify(err, null, 2));

            // --- LÓGICA DE ERROR MEJORADA ---
            // La función de Supabase ahora devuelve errores claros y accionables.
            // Buscamos el mensaje de error directamente.
            let alertMessage = "Ocurrió un error inesperado al contactar al servidor.";

            if (err.message && err.message.includes('Failed to fetch')) {
                 alertMessage = `Error de conexión: No se pudo comunicar con el servidor. Verificá tu conexión a internet.`;
            } else {
                // El error de la función `invoke` anida el error real devuelto por el backend.
                const functionError = err.context?.json?.error;
                if (functionError) {
                    // Este es el mensaje que definimos en nuestra función de Deno.
                    alertMessage = functionError;
                } else if (err.message) {
                    alertMessage = `Error al crear la preferencia de pago: ${err.message}`;
                }
            }
            
            ui.showModal('Error', alertMessage);
        } finally {
            setLoadingPlanId(null);
        }
    };
    
    const handleGestionarSuscripcion = async (action: 'consultar' | 'cancelar') => {
        if (!club) return;
        
            if (action === 'cancelar') {
                ui.showModal('Confirmar cancelación', '¿Estás seguro de que querés cancelar tu suscripción? Esta acción no se puede deshacer y tu plan se dará de baja al final del período de facturación actual.', async () => {
                    setIsManaging(true);
                    setErrorGestion(null);
                    try {
                        const { data, error } = await supabase.functions.invoke('gestionar-suscripcion', {
                            body: { clubId: club.id, action }
                        });

                        if (error) throw error;
                        if (data.error) throw new Error(data.error);

                        ui.showToast('info', data.message);

                    } catch (err: any) {
                        console.error("Error completo al gestionar suscripción:", JSON.stringify(err, null, 2));

                        let detailedError = "Ocurrió un error inesperado. Revisa la consola para más detalles.";

                        if (err.context && err.context.json && err.context.json.error) {
                            detailedError = err.context.json.error;
                        } else if (err.message) {
                            if (err.message.includes('non-2xx status code')) {
                                detailedError = `Hubo un problema de comunicación con el servidor (código ${err.context?.status || 'desconocido'}). Por favor, verificá tu conexión e intentá de nuevo.`;
                            } else {
                                detailedError = err.message;
                            }
                        }

                        setErrorGestion(detailedError);
                    } finally {
                        setIsManaging(false);
                    }
                });

                return;
            }

            setIsManaging(true);
            setErrorGestion(null);

            try {
                const { data, error } = await supabase.functions.invoke('gestionar-suscripcion', {
                    body: { clubId: club.id, action }
                });

                if (error) throw error;
                if (data.error) throw new Error(data.error);

                ui.showToast('info', data.message);

            } catch (err: any) {
            console.error("Error completo al gestionar suscripción:", JSON.stringify(err, null, 2));
            
                let detailedError = "Ocurrió un error inesperado. Revisa la consola para más detalles.";

                if (err.context && err.context.json && err.context.json.error) {
                    detailedError = err.context.json.error;
                } else if (err.message) {
                    if (err.message.includes('non-2xx status code')) {
                        detailedError = `Hubo un problema de comunicación con el servidor (código ${err.context?.status || 'desconocido'}). Por favor, verificá tu conexión e intentá de nuevo.`;
                    } else {
                        detailedError = err.message;
                    }
                }

                setErrorGestion(detailedError);
        } finally {
            setIsManaging(false);
        }
    };

    const planActual = planes.find(p => p.nombre === club.plan);
    const suscripcionActiva = club.suscripcion_id && club.suscripcion_estado !== 'canceled';
    
    return (
        <div className="space-y-8">
            {paymentSuccessMessage && (
                <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-md shadow-md flex justify-between items-center" role="alert">
                    <div className="flex items-center">
                        <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path></svg>
                        <p className="font-bold">{paymentSuccessMessage}</p>
                    </div>
                    <button onClick={() => setPaymentSuccessMessage(null)} className="text-green-800 hover:text-green-600 font-bold text-2xl">&times;</button>
                </div>
            )}
            <div>
                <h2 className="text-3xl font-bold text-gray-800">Mi Suscripción</h2>
                <p className="text-gray-600 mt-1">Gestioná tu plan, facturación y revisá el uso de tu cuenta.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Tu Plan Actual</h3>
                    <div className="space-y-3 text-gray-700">
                        <div className="flex justify-between items-center">
                            <span className="font-medium">Plan:</span>
                            <span className="text-lg font-bold text-blue-600">{club.plan}</span>
                        </div>
                         <div className="flex justify-between items-center">
                            <span className="font-medium">Estado:</span>
                            <SuscripcionBadge status={club.suscripcion_estado} />
                        </div>
                        <div className="flex justify-between items-center">
                            <span className="font-medium">Precio:</span>
                            <span className="font-semibold">${planActual?.precio?.toLocaleString('es-AR')}/{planActual?.intervalo}</span>
                        </div>
                        {club.suscripcion_estado === 'trialing' && club.periodo_actual_fin && (
                             <div className="flex justify-between items-center">
                                <span className="font-medium">Prueba finaliza:</span>
                                <span className="font-semibold">{new Date(club.periodo_actual_fin).toLocaleDateString('es-AR')}</span>
                            </div>
                        )}
                         {club.suscripcion_estado === 'active' && club.periodo_actual_fin && (
                             <div className="flex justify-between items-center">
                                <span className="font-medium">Próximo cobro:</span>
                                <span className="font-semibold">{new Date(club.periodo_actual_fin).toLocaleDateString('es-AR')}</span>
                            </div>
                        )}
                        {club.suscripcion_id &&
                            <button 
                                onClick={() => handleGestionarSuscripcion('consultar')}
                                disabled={isManaging}
                                className="w-full text-sm mt-2 text-blue-600 hover:underline disabled:text-gray-400 disabled:cursor-wait"
                            >
                                {isManaging ? 'Verificando...' : 'Verificar estado con Mercado Pago'}
                            </button>
                        }
                    </div>
                </div>

                <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-md">
                     <h3 className="text-xl font-semibold text-gray-800 mb-4">Uso del Plan</h3>
                     {planActual && <UsageProgressBar count={socios.length} limit={planActual.limite_socios} />}
                     <p className="text-xs text-gray-500 mt-2">
                        {planActual?.limite_socios === -1 
                            ? "Tenés socios ilimitados."
                            : `Podés registrar hasta ${planActual?.limite_socios} socios. Si necesitás más, considerá cambiar de plan.`
                        }
                    </p>
                </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Cambiar de Plan</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {planes.filter(p => p.activo).map(p => {
                        const isCurrentPlan = club.plan === p.nombre;
                        const isActiveSubscription = club.suscripcion_estado === 'active' || club.suscripcion_estado === 'trialing';
                        const isButtonDisabled = (isCurrentPlan && isActiveSubscription) || loadingPlanId !== null;
                        
                        let buttonText = 'Cambiar a este plan';
                        let buttonClass = 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400';

                        if (isCurrentPlan) {
                            if (isActiveSubscription) {
                                buttonText = 'Plan Actual';
                                buttonClass = 'bg-gray-300 text-gray-600';
                            } else {
                                buttonText = 'Reactivar este Plan';
                                buttonClass = 'bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400';
                            }
                        }
                         if (loadingPlanId === p.id) {
                            buttonText = 'Redirigiendo...';
                        }

                        return (
                            <div key={p.id} className={`p-6 rounded-lg border-2 flex flex-col text-center ${isCurrentPlan ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                                <h4 className="font-bold text-xl text-gray-800">{p.nombre}</h4>
                                
                                <div className="my-4">
                                    <span className="text-4xl font-extrabold text-gray-900">${p.precio}</span>
                                    <span className="text-base font-medium text-gray-500">/{p.intervalo}</span>
                                </div>
                                
                                <p className="text-sm text-gray-600 mb-6 flex-grow">
                                    {p.limite_socios === -1 ? 'Socios ilimitados' : `Hasta ${p.limite_socios} socios`}
                                </p>
                                
                                <button 
                                    onClick={() => handlePlanChange(p)}
                                    disabled={isButtonDisabled}
                                    className={`w-full font-semibold py-2 rounded-md transition-colors text-sm disabled:cursor-not-allowed ${buttonClass}`}
                                >
                                    {buttonText}
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Facturación y Gestión</h3>
                 <div className="flex flex-wrap gap-4 items-center mb-6">
                    <button 
                        onClick={() => handleGestionarSuscripcion('cancelar')}
                        disabled={!suscripcionActiva || isManaging}
                        className="bg-transparent border border-red-500 text-red-500 px-5 py-2 rounded-md hover:bg-red-50 font-semibold disabled:bg-gray-200 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                        {isManaging ? 'Procesando...' : 'Cancelar Suscripción'}
                    </button>
                 </div>
                 {errorGestion && <p className="text-red-600 text-sm mb-4">{errorGestion}</p>}
                <h4 className="text-lg font-medium text-gray-700 mb-2">Historial de Facturas</h4>
                <div className="overflow-x-auto border rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                             <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nº Factura</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Descargar</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                           {facturas.length > 0 ? facturas.map(factura => (
                               <tr key={factura.id}>
                                   <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">INV-{new Date(factura.creado_en).getFullYear()}-{factura.id.toString().padStart(4, '0')}</td>
                                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{new Date(factura.creado_en).toLocaleDateString('es-AR')}</td>
                                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${factura.metadata?.monto?.toLocaleString('es-AR') || 'N/A'}</td>
                                   <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                            Pagada
                                        </span>
                                   </td>
                                   <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                       <a href="#" className="text-blue-600 hover:text-blue-900">Descargar</a>
                                   </td>
                               </tr>
                           )) : (
                                <tr>
                                    <td colSpan={5} className="text-center py-6 text-gray-500">
                                        No hay facturas para mostrar.
                                    </td>
                                </tr>
                           )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
